import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { ordersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { formatPrice } from '../lib/utils';
import { Package, Clock, CheckCircle2, XCircle, Loader2, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const STATUS_MAP = {
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
  initiated: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: Clock },
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ordersAPI.getMyOrders()
      .then(res => setOrders(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="orders-page">
        <div>
          <h1 className="text-2xl font-bold">My Orders</h1>
          <p className="text-stone-500 text-sm mt-1">View your purchase history and order status</p>
        </div>

        {orders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <h3 className="font-semibold text-stone-700 mb-1">No orders yet</h3>
              <p className="text-sm text-stone-400 mb-4">Browse services to get started</p>
              <Link to="/services">
                <Button variant="outline" className="gap-2">
                  <ShoppingBag className="w-4 h-4" /> Browse Services
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.map(order => {
              const st = STATUS_MAP[order.payment_status] || STATUS_MAP.pending;
              const Icon = st.icon;
              return (
                <Card key={order.id} data-testid={`order-${order.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium">
                        Order #{order.id?.slice(0, 8)}
                      </CardTitle>
                      <Badge className={`${st.color} gap-1`}>
                        <Icon className="w-3 h-3" /> {st.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-stone-600">
                          {item.service_name} — {item.tier_name}
                        </span>
                        <span className="font-medium">{formatPrice(item.price)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t text-sm">
                      <span className="text-stone-500">Total</span>
                      <span className="font-bold">{formatPrice(order.total)}</span>
                    </div>
                    <div className="text-xs text-stone-400 pt-1">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : ''}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
